import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import DivTableSkeleton, { DivTableColumn } from '../skeleton/DivTableSkeleton';
import styles from './BonusTable.module.scss';

export interface Bonus {
  id: number;
  source: string;
  type: string;
  amount: string;
  currencyCode: string;
  createdAt: string;
  details?: {
    source?: string;
    spinLevelKey?: string;
    promoCodeId?: number;
    code?: string;
    spinType?: string;
    [key: string]: any;
  };
  spinType?: string;
}

interface BonusTableProps {
  bonuses: Bonus[];
  loading: boolean;
}

const BonusTable: FC<BonusTableProps> = ({ bonuses, loading }) => {
  const { t } = useTranslation();

  const tableColumns: DivTableColumn[] = useMemo(
    () => [
      { label: t('myBonuses.columns.bonusType'), width: '70%' },
      { label: t('myBonuses.columns.amount'), width: '50%' },
      { label: t('myBonuses.columns.details'), width: '60%' },
      { label: t('myBonuses.columns.date'), width: '80%' },
    ],
    [t],
  );

  if (!loading && bonuses.length === 0) {
    return <div className={styles.noDataWrapper}>{t('myBonuses.noBonusesFound')}</div>;
  }

  const formatSpinType = (spinType: string) => {
    return spinType
      .replace(/[_-]/g, ' ')
      .split(' ')
      .filter(Boolean)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  const getBadgeText = (type: string) => {
    const lower = type?.toLowerCase() || '';
    if (lower.includes('site_wheel')) return t('myBonuses.types.luckyWheel');
    if (lower === 'promo') return t('myBonuses.types.promo');
    if (lower === 'wheel') return t('myBonuses.types.wheel');
    if (lower === 'rakeback' || lower === 'rackeback') return t('myBonuses.types.rakeback');
    if (lower === 'monthly_bonus') return t('myBonuses.types.monthlyBonus');
    if (lower === 'weekly_boost') return t('myBonuses.types.weeklyBoost');
    if (lower === 'pre_monthly_bonus') return t('myBonuses.types.preMonthlyBonus');
    if (lower === 'quarterly_bonus') return t('myBonuses.types.quarterlyBonus');
    if (lower === 'free_spin') return t('myBonuses.types.freeSpin');
    if (lower === 'lossback') return t('myBonuses.types.lossback');
    if (lower === 'vip_bonus') return t('myBonuses.types.vipBonus');
    return type.replace(/_/g, ' ').toUpperCase();
  };

  const renderDetails = (bonus: Bonus) => {
    const spinType = bonus.details?.spinType || bonus.spinType;
    if (spinType) {
      const spinTypeClass = spinType.toLowerCase().replace(/-/g, '_');
      const label =
        spinTypeClass === 'cash'
          ? t('myBonuses.types.cash')
          : spinTypeClass === 'deposit_match'
            ? t('myBonuses.types.depositMatch')
            : spinTypeClass === 'free_spin'
              ? t('myBonuses.types.freeSpin')
              : formatSpinType(spinType);
      return (
        <span className={`${styles.detailBadge} ${styles[spinTypeClass] || ''}`}>
          {label}
        </span>
      );
    }

    if (bonus.type === 'promo' && bonus.details?.code) {
      return (
        <span className={styles.description}>
          {t('myBonuses.details.code', { code: bonus.details.code })}
        </span>
      );
    }

    if (bonus.details?.spinLevelKey) {
      return (
        <span className={styles.description}>
          {t('myBonuses.details.level', { level: bonus.details.spinLevelKey.replace(/_/g, ' ') })}
        </span>
      );
    }

    return null;
  };

  return (
    <div className={styles.tableContainer}>
      <div className={styles.tableHeader}>
        {tableColumns.map((col) => (
          <div key={col.label} className={styles.th}>
            {col.label}
          </div>
        ))}
      </div>
      <div className={styles.tableBody}>
        {loading ? (
          <DivTableSkeleton
            rows={5}
            rowClassName={styles.tableRow}
            cellClassName={styles.td}
            labelClassName={styles.mobileLabel}
            columns={tableColumns}
          />
        ) : (
          bonuses.map((bonus) => {
            const isSiteWheel = bonus.type?.toLowerCase().includes('site_wheel');
            const badgeClass = isSiteWheel
              ? styles.site_wheel
              : styles[bonus.type.toLowerCase()] || '';
            const badgeText = getBadgeText(bonus.type);

            return (
              <div key={bonus.id} className={styles.tableRow}>
                <div className={styles.td}>
                  <span className={styles.mobileLabel}>{t('myBonuses.columns.bonusType')}</span>
                  <div className={styles.typeInfo}>
                    <span className={`${styles.typeBadge} ${badgeClass}`}>
                      {badgeText}
                    </span>
                  </div>
                </div>
                <div className={styles.td}>
                  <span className={styles.mobileLabel}>{t('myBonuses.columns.amount')}</span>
                  <span className={styles.amount}>
                    {bonus.amount} {bonus.currencyCode}
                  </span>
                </div>
                <div className={styles.td}>
                  <span className={styles.mobileLabel}>{t('myBonuses.columns.details')}</span>
                  {renderDetails(bonus)}
                </div>
                <div className={styles.td}>
                  <span className={styles.mobileLabel}>{t('myBonuses.columns.date')}</span>
                  <span className={styles.date}>
                    {dayjs(bonus.createdAt).format('DD.MM.YYYY, HH:mm:ss')}
                  </span>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default BonusTable;
